/**
 * F 136 — the four generic layout renderers: `card`, `table`, `dense`,
 * `detail`.
 *
 * These exist so a consumer can reach for a common shape WITHOUT writing a
 * renderer. FCr ships the mechanism; the consumer still supplies the
 * vocabulary (property ids, labels, sections) — the same split FCr uses for
 * icon sets and pickers.
 *
 * ALL FOUR honour the view contract identically, and none of them re-derives
 * it:
 *   - which properties show        -> `viewConfig.visiblePropertyIds`
 *   - in what order                -> `viewConfig.propertyOrder`
 *   - when the view says neither   -> `PropertyDefinition.visible` / `.position`
 * That resolution is `resolveVisibleProperties()`, the SAME function
 * `CardRenderer` (F 135) and `applyViewConfig` use. A layout that invented
 * its own would be a second source of truth — the exact failure F 131 fixed.
 *
 * No Mantine: FCr's `<Button>` does not type-check under this project's React
 * types (see F 135's notes), so these use plain elements styled with FCr's
 * design tokens.
 */

import React, { useMemo, useState } from 'react';
import { resolveVisibleProperties } from '../../core/views/viewConfig';
import type {
  ViewConfig,
  CardLayoutOptions,
  TableLayoutOptions,
  DenseLayoutOptions,
  DetailLayoutOptions,
  DetailSection,
} from '../../core/views/viewConfig';
import type { PropertyDefinition, PropertyValue } from '../../types/models/Property';

// ─── shared ────────────────────────────────────────────────────────────────

export interface ViewRendererRow {
  id: string;
  title?: string;
  icon?: string;
  properties?: PropertyValue[];
  [k: string]: unknown;
}

const valueOf = (row: ViewRendererRow, propertyId: string): unknown =>
  (row.properties ?? []).find((p) => p?.propertyId === propertyId)?.value;

const display = (v: unknown): string => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) return v.map(display).filter(Boolean).join(', ');
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>;
    for (const k of ['name', 'label', 'title', 'text', 'url']) {
      if (typeof o[k] === 'string') return o[k] as string;
    }
    return '';
  }
  return String(v);
};

const tok = {
  surface: 'var(--fc-bg-surface, #FFFFFF)',
  border: 'var(--fc-border-primary, #E8E0D3)',
  faint: 'var(--fc-border-secondary, #E8E0D3)',
  text: 'var(--fc-text-primary, #2A2622)',
  muted: 'var(--fc-text-secondary, #6B6257)',
  hint: 'var(--fc-text-hint, #9C9285)',
  alt: 'var(--fc-bg-secondary, #F5F0E8)',
  accent: 'var(--fc-accent-primary, #B8632E)',
};

/** The one resolution path every renderer shares. */
function useVisible(view: ViewConfig, defs: PropertyDefinition[]): PropertyDefinition[] {
  return useMemo(() => resolveVisibleProperties(view, defs), [view, defs]);
}

// ──��� 1. card — vertical stack of self-contained items ────────────────────────

export interface CardStackRendererProps {
  items: ViewRendererRow[];
  viewConfig: ViewConfig;
  propertyDefs: PropertyDefinition[];
  onSelect?: (id: string) => void;
  className?: string;
  'data-testid'?: string;
}

/**
 * Unlike F 135's single-item `CardRenderer`, this renders a STACK. Per item:
 * an optional left colour stripe from `stripePropId`, the title, an optional
 * subtitle, an optional indicator ring, and an optional checkbox.
 */
export function CardStackRenderer({
  items, viewConfig, propertyDefs, onSelect, className, 'data-testid': testId = 'card-layout',
}: CardStackRendererProps): React.ReactElement {
  const visible = useVisible(viewConfig, propertyDefs);
  const opts = (viewConfig.layoutOptions ?? {}) as CardLayoutOptions;
  // `stripePropId` / `subtitlePropId` etc. are chrome, not body — exclude them
  // from the body rows so they are not printed twice.
  const chromeIds = new Set([opts.stripePropId, opts.indicatorPropId, opts.checkboxPropId, opts.subtitlePropId].filter(Boolean) as string[]);
  const body = visible.filter((d) => !chromeIds.has(d.id));

  return (
    <div className={className} data-fc-layout="card" data-testid={testId}
         style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {items.map((item) => {
        const stripe = opts.stripePropId ? display(valueOf(item, opts.stripePropId)) : '';
        return (
          <div
            key={item.id}
            data-fc-card-row={item.id}
            data-fc-card-props={body.length}
            onClick={onSelect ? () => onSelect(item.id) : undefined}
            style={{
              display: 'flex', gap: 10, alignItems: 'stretch',
              background: tok.surface, border: `1px solid ${tok.border}`,
              borderRadius: 10, padding: 10, cursor: onSelect ? 'pointer' : undefined,
            }}
          >
            {stripe ? (
              <span data-fc-card-stripe="" aria-hidden="true"
                    style={{ width: 3, borderRadius: 3, background: tok.accent, flexShrink: 0 }} />
            ) : null}

            {opts.checkboxPropId ? (
              <input
                type="checkbox"
                data-fc-card-checkbox={opts.checkboxPropId}
                checked={valueOf(item, opts.checkboxPropId) === true}
                readOnly
                onClick={(e) => e.stopPropagation()}
                aria-label={`Toggle ${item.title ?? item.id}`}
              />
            ) : null}

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <strong data-fc-card-title="" style={{ fontSize: 13.5, color: tok.text }}>
                  {item.icon ? `${item.icon} ` : ''}{item.title ?? item.id}
                </strong>
                {opts.indicatorPropId ? (
                  <span data-fc-card-indicator="" title={opts.indicatorPropId}
                        style={{ width: 8, height: 8, borderRadius: '50%',
                                 border: `2px solid ${tok.accent}`, display: 'inline-block' }} />
                ) : null}
              </div>
              {opts.subtitlePropId ? (
                <div data-fc-card-subtitle="" style={{ fontSize: 12, color: tok.muted }}>
                  {display(valueOf(item, opts.subtitlePropId))}
                </div>
              ) : null}
              <dl style={{ margin: '4px 0 0', display: 'flex', flexDirection: 'column', gap: 2 }}>
                {body.map((d) => {
                  const v = display(valueOf(item, d.id));
                  if (!v) return null;
                  return (
                    <div key={d.id} data-fc-card-prop={d.id} style={{ display: 'flex', gap: 6, fontSize: 12 }}>
                      <dt style={{ margin: 0, color: tok.muted }}>{d.name || d.id}</dt>
                      <dd style={{ margin: 0, color: tok.text }}>{v}</dd>
                    </div>
                  );
                })}
              </dl>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── 2. table — grid, sortable columns ──────────────────────────────────────

export interface TableLayoutProps {
  items: ViewRendererRow[];
  viewConfig: ViewConfig;
  propertyDefs: PropertyDefinition[];
  /** Called with the column id when a header is clicked. */
  onSort?: (propertyId: string) => void;
  className?: string;
  'data-testid'?: string;
}

export function TableLayoutRenderer({
  items, viewConfig, propertyDefs, onSort, className, 'data-testid': testId = 'table-layout',
}: TableLayoutProps): React.ReactElement {
  const visible = useVisible(viewConfig, propertyDefs);
  const opts = (viewConfig.layoutOptions ?? {}) as TableLayoutOptions;
  const activeSort = viewConfig.sort?.[0];

  return (
    <div className={className} data-fc-layout="table" data-testid={testId}
         style={{ overflowX: 'auto', border: `1px solid ${tok.border}`, borderRadius: 10 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
        <thead>
          <tr>
            <th scope="col" style={{ textAlign: 'left', padding: '8px 10px', background: tok.alt, color: tok.muted, fontWeight: 600 }}>
              Item
            </th>
            {visible.map((d) => (
              <th key={d.id} scope="col" data-fc-th={d.id}
                  aria-sort={activeSort?.propertyId === d.id ? (activeSort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                  style={{ textAlign: 'left', padding: '8px 10px', background: tok.alt, color: tok.muted, fontWeight: 600 }}>
                {(() => {
                  const caret = activeSort?.propertyId === d.id
                    ? (activeSort.dir === 'asc' ? ' ↑' : ' ↓') : '';
                  // The caret is state, not an affordance: a read-only table
                  // must still show how it is sorted.
                  if (!onSort) return <span data-fc-sort-label={d.id}>{d.name || d.id}{caret}</span>;
                  return (
                    <button type="button" data-fc-sort={d.id} onClick={() => onSort(d.id)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', font: 'inherit', color: 'inherit', padding: 0 }}>
                      {d.name || d.id}{caret}
                    </button>
                  );
                })()}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id} data-fc-tr={item.id} style={{ borderTop: `1px solid ${tok.faint}` }}>
              <td data-fc-td="__title" style={{ padding: '8px 10px', color: tok.text, fontWeight: 500 }}>
                {item.icon ? `${item.icon} ` : ''}{item.title ?? item.id}
              </td>
              {visible.map((d) => {
                const v = display(valueOf(item, d.id));
                return (
                  <td key={d.id} data-fc-td={d.id}
                      contentEditable={opts.editable === true}
                      suppressContentEditableWarning
                      style={{ padding: '8px 10px', color: tok.text }}>
                    {v}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── 3. dense — one-line grouped rows, no chrome ────────────────────────────

export interface DenseLayoutProps {
  items: ViewRendererRow[];
  viewConfig: ViewConfig;
  propertyDefs: PropertyDefinition[];
  className?: string;
  'data-testid'?: string;
}

export function DenseLayoutRenderer({
  items, viewConfig, propertyDefs, className, 'data-testid': testId = 'dense-layout',
}: DenseLayoutProps): React.ReactElement {
  const visible = useVisible(viewConfig, propertyDefs);
  const opts = (viewConfig.layoutOptions ?? {}) as DenseLayoutOptions;
  // A dense row is a one-line row: the TITLE is the row. `valuePropId` opts
  // into something else explicitly. Falling back to the first visible
  // property showed a status where the name should be.
  const titleProp = opts.valuePropId;

  // Grouped when a groupPropId is configured; otherwise one unnamed group.
  const groups = useMemo(() => {
    if (!opts.groupPropId) return [{ key: '__all', label: '', rows: items }];
    const m = new Map<string, ViewRendererRow[]>();
    for (const it of items) {
      const k = display(valueOf(it, opts.groupPropId)) || '—';
      (m.get(k) ?? m.set(k, []).get(k)!).push(it);
    }
    return [...m.entries()].map(([key, rows]) => ({ key, label: key, rows }));
  }, [items, opts.groupPropId]);

  return (
    <div className={className} data-fc-layout="dense" data-testid={testId}
         style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {groups.map((g) => (
        <div key={g.key} data-fc-dense-group={g.key}>
          {g.label ? (
            <div data-fc-dense-group-header="" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', color: tok.hint, padding: '4px 2px' }}>
              {g.label}
            </div>
          ) : null}
          {g.rows.map((it) => {
            const primary = titleProp
              ? (display(valueOf(it, titleProp)) || it.title || it.id)
              : (it.title ?? it.id);
            return (
              <div key={it.id} data-fc-dense-row={it.id}
                   style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 2px', fontSize: 12.5, color: tok.text, borderBottom: `1px solid ${tok.faint}` }}>
                {opts.checkbox ? (
                  <input type="checkbox" data-fc-dense-checkbox={it.id} checked={valueOf(it, visible[0]?.id ?? '') === true} readOnly
                         aria-label={`Toggle ${it.title ?? it.id}`} />
                ) : null}
                <span data-fc-dense-primary="" style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {it.icon ? `${it.icon} ` : ''}{primary}
                </span>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ─── 4. detail — structured single-item page ───────────────────────────────

export interface DetailLayoutProps {
  item: ViewRendererRow;
  viewConfig: ViewConfig;
  propertyDefs: PropertyDefinition[];
  /** Preset applied when the view configures no sections. Composable —
   *  this is how a consumer makes `detail` into their own shape. */
  sections?: DetailSection[];
  className?: string;
  'data-testid'?: string;
}

export function DetailLayoutRenderer({
  item, viewConfig, propertyDefs, sections, className, 'data-testid': testId = 'detail-layout',
}: DetailLayoutProps): React.ReactElement {
  const opts = (viewConfig.layoutOptions ?? {}) as DetailLayoutOptions;
  const configured = sections ?? opts.sections ?? [];
  const [done, setDone] = useState<Record<string, boolean>>({});

  // A configured section may source its body from a property.
  const bodyOf = (sec: DetailSection): string => {
    if (sec.body !== undefined) return sec.body;
    if (sec.propertyId) return display(valueOf(item, sec.propertyId));
    return '';
  };
  const cover = opts.coverImageUrl;

  return (
    <article className={className} data-fc-layout="detail" data-testid={testId}
             style={{ display: 'flex', flexDirection: 'column', gap: 12, color: tok.text }}>
      <h2 data-fc-detail-title="" style={{ margin: 0, fontSize: 18 }}>
        {item.icon ? `${item.icon} ` : ''}{item.title ?? item.id}
      </h2>

      {cover ? (
        <img data-fc-detail-cover="" src={cover} alt=""
             style={{ width: '100%', maxHeight: 220, objectFit: 'cover', borderRadius: 10 }} />
      ) : null}

      {configured.map((sec, i) => {
        const key = `${item.id}:${i}`;
        const body = bodyOf(sec);
        return (
          <section key={key} data-fc-detail-section={sec.title}
                   style={{ border: `1px solid ${tok.border}`, borderRadius: 10, padding: 12 }}>
            <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0, fontSize: 13.5 }}>{sec.title}</h3>
              {sec.state && Object.keys(sec.state).length > 0 ? (
                <label data-fc-detail-state={sec.title} style={{ fontSize: 11.5, color: tok.muted, display: 'inline-flex', gap: 5, alignItems: 'center' }}>
                  <input type="checkbox" checked={done[key] === true}
                         onChange={(e) => setDone((p) => ({ ...p, [key]: e.target.checked }))} />
                  {String(sec.state.label ?? 'done')}
                </label>
              ) : null}
            </header>
            {sec.imageUrl ? (
              <img data-fc-detail-image="" src={sec.imageUrl} alt="" style={{ maxWidth: '100%', borderRadius: 8, marginTop: 8 }} />
            ) : null}
            {body ? (
              <p data-fc-detail-body={sec.title} style={{ margin: '6px 0 0', fontSize: 13, color: tok.muted, whiteSpace: 'pre-wrap' }}>
                {body}
              </p>
            ) : null}
          </section>
        );
      })}
    </article>
  );
}
